import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Refetches server-backed data when:
 * - The tab becomes visible again after being hidden (e.g. user toggled settings in Manifesto in another tab).
 * - React Router's location.key changes while this screen stays mounted (e.g. search params navigation).
 * - The page is restored from the browser back/forward cache (bfcache), where React may not remount.
 */
export function useRefetchOnVisibilityAndRoute(refetch: () => void, enabled: boolean) {
	const refetchRef = useRef(refetch);
	refetchRef.current = refetch;

	const wasHiddenRef = useRef(false);
	const initialLocationKeyRef = useRef<string | null>(null);
	const location = useLocation();

	useEffect(() => {
		if (!enabled) {
			return;
		}
		const onVisibility = () => {
			if (document.visibilityState === 'hidden') {
				wasHiddenRef.current = true;
				return;
			}
			if (document.visibilityState === 'visible' && wasHiddenRef.current) {
				wasHiddenRef.current = false;
				refetchRef.current();
			}
		};
		document.addEventListener('visibilitychange', onVisibility);
		return () => document.removeEventListener('visibilitychange', onVisibility);
	}, [enabled]);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		const onPageShow = (e: PageTransitionEvent) => {
			if (e.persisted) {
				refetchRef.current();
			}
		};
		window.addEventListener('pageshow', onPageShow);
		return () => window.removeEventListener('pageshow', onPageShow);
	}, [enabled]);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		if (initialLocationKeyRef.current === null) {
			initialLocationKeyRef.current = location.key;
			return;
		}
		if (initialLocationKeyRef.current !== location.key) {
			initialLocationKeyRef.current = location.key;
			refetchRef.current();
		}
	}, [enabled, location.key]);
}
